import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import {
  AppToWebEventPayload,
  AppToWebEvents,
  BiometricAvailablePayload,
  BiometricCredentialsPayload,
  sendEventToAppWrapper,
  subscribeToAppWrapperEvents,
} from '@/utils/app-wrapper-utils';
import { CustomWebToAppEvents } from '@/models/enum/app-wrapper.enum';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import { BffAuthResponse, BffErrorCode } from '@lucky7ventures/bff-types';
import { loginSuccess } from '@/store/actions/auth';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { setBiometricChoice } from '@/utils/biometric-storage';
import { selectIsInAppWrapper } from '@/store/selectors/commonSelectors';

// Module-level variable to track biometric availability
// Resets on page reload, which is correct since mobile sends fresh status each time
let biometricAvailable: boolean | null = null;

// Track if current login attempt is from biometric credentials
// Used to determine if we should notify mobile on auth failure
let currentLoginIsBiometric = false;

const isBiometricCredentials = (
  params: AppToWebEventPayload,
): params is BiometricCredentialsPayload => {
  return 'email' in params && 'password' in params;
};

const isBiometricAvailable = (
  params: AppToWebEventPayload,
): params is BiometricAvailablePayload => {
  return 'available' in params;
};

/**
 * Check if biometric login is available on the device.
 * Returns true only if mobile app has confirmed biometrics is supported and enabled.
 */
export const isBiometricLoginAvailable = (): boolean => {
  return biometricAvailable === true;
};

export const useBiometricLogin = (): void => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { request } = useBffApiRequest<BffAuthResponse>();
  const hasRequestedStatus = useRef(false);
  const isInAppWrapper = useSelector(selectIsInAppWrapper);

  useEffect(() => {
    if (!isInAppWrapper) {
      return;
    }

    const handleAppEvent = (event: AppToWebEvents, params: AppToWebEventPayload) => {
      if (event === 'biometric_available' && isBiometricAvailable(params)) {
        biometricAvailable = params.available;
      }

      if (event === 'biometric_credentials' && isBiometricCredentials(params) && !isAuthenticated) {
        currentLoginIsBiometric = true;

        request({
          apiMethod: BffApiService.login,
          payload: {
            username: params.email,
            password: params.password,
          },
          successCallback: (data: BffAuthResponse) => {
            currentLoginIsBiometric = false;
            if (data.result === 'SUCCESSFUL') {
              dispatch(loginSuccess(data.token));
              triggerGTag(GTagEvents.login_success);
            }
            // Note: 2FA case not handled for biometric login
            // User would need to use regular login flow for 2FA accounts
          },
          errorCallback: error => {
            if (currentLoginIsBiometric && error?.code === BffErrorCode.GENERIC_AUTH_ERROR) {
              sendEventToAppWrapper(CustomWebToAppEvents.biometric_login_failed);
              setBiometricChoice(null);
            }
            currentLoginIsBiometric = false;
          },
        });
      }

      if (event === 'biometric_credentials_cleared') {
        setBiometricChoice(null);
      }
    };

    const unsubscribe = subscribeToAppWrapperEvents(handleAppEvent);

    // Signal to mobile that web is ready (only once per page load)
    // Mobile uses this to respond with biometric_available and optionally prompt biometrics
    if (!hasRequestedStatus.current) {
      sendEventToAppWrapper(CustomWebToAppEvents.request_biometric_status);
      hasRequestedStatus.current = true;
    }

    return () => {
      unsubscribe();
    };
  }, [isAuthenticated, dispatch, request]);
};
